import { Box, Button, Divider, Grid, Icon, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { GetLogedInMembers } from "../../api/hooks";
import { IMemberLoginHistory } from "../../api/types";
import MainCard from "components/MainCard";
import { BarChartOutlined } from "@ant-design/icons";
import FormLoader from "components/Containers/FormLoader";
import LoginHistoryChart from "./LoginHistoryChart";
import useLoginHistoryStore from "../../store/useLoginHistoryStore";
import LoginHistoryTable from "./LoginHistoryTable";

const LoginHistoryStatistics = () => {
  const defaultFromDate = dayjs().subtract(1, 'year');
  const defaultToDate = dayjs();
  const { handleFiltering } = useLoginHistoryStore();

  const [filterModel, setFilterModel] = useState<any>({
    filter: {
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    },
  });

  const [data, setData] = useState<IMemberLoginHistory>();

  const { control, handleSubmit, reset } = useForm<any>({
    defaultValues: {
      fromDate: defaultFromDate,
      toDate: defaultToDate,
    },
  });

  const onSubmit = handleSubmit((filter) => {
    const fromVal = filter.fromDate ? filter.fromDate : null;
    const toVal = filter.toDate ? filter.toDate : null;

    setFilterModel({
      filter: {
        fromDate: fromVal,
        toDate: toVal,
      },
    });

    handleFiltering({
      fromDate: fromVal ? dayjs(fromVal).toDate() : null,
      toDate: toVal ? dayjs(toVal).toDate() : null,
    });
  });

  const loginHistoryQuery = GetLogedInMembers(filterModel);

  useEffect(() => {
    if (loginHistoryQuery.data?.status === 200) {
      setData(loginHistoryQuery?.data?.apiData);
    }
  }, [loginHistoryQuery.data]);

  useEffect(() => {
    loginHistoryQuery.refetch();
  }, [filterModel]);

  const handleClear = () => {
    const defaultFromDate = dayjs().subtract(1, 'year');
    const defaultToDate = dayjs();

    setFilterModel((prev: any) => ({
      ...prev,
      filter: {
        fromDate: defaultFromDate,
        toDate: defaultToDate,
      },
    }));

    handleFiltering({
      fromDate: defaultFromDate.toDate(),
      toDate: defaultToDate.toDate(),
    });

    reset({ fromDate: defaultFromDate, toDate: defaultToDate });
  };

  return (
    <>
      <MainCard sx={{ my: 1 }}>
        <Typography variant="h3">
          <Icon sx={{ marginRight: "5px" }}>
            <BarChartOutlined />
          </Icon>
          Member Engagement (Login Activity)
        </Typography>
        <Divider sx={{ my: 2 }} />
        <Box component="form" noValidate sx={{ my: 3 }} onSubmit={onSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={6} md={6} lg={4}>
              <Controller
                name="fromDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    sx={{ width: "100%" }}
                    label="From Date"
                    format="DD-MM-YYYY"
                    value={value ? dayjs(value) : null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                        fullWidth: false,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid item xs={6} md={6} lg={4}>
              <Controller
                name="toDate"
                control={control}
                render={({
                  field: { value, onChange },
                  fieldState: { error },
                }) => (
                  <DatePicker
                    sx={{ width: "100%" }}
                    label="To Date"
                    format="DD-MM-YYYY"
                    value={value ? dayjs(value) : null}
                    onChange={onChange}
                    slotProps={{
                      textField: {
                        error: !!error,
                        helperText: error?.message,
                        fullWidth: false,
                      },
                    }}
                  />
                )}
              />
            </Grid>
            <Grid
              item
              display={"flex"}
              alignItems={"center"}
              xs={6}
              md={4}
              lg={3}
            >
              <Button type="submit" variant="contained">
                Show
              </Button>
              <Button
                type="submit"
                variant="contained"
                color="secondary"
                sx={{ ml: 1 }}
                onClick={handleClear}
              >
                Clear
              </Button>
            </Grid>
          </Grid>
        </Box>
        <Grid container spacing={2.5}>
          <Grid item sm={12} md={12} xs={12}>
            {loginHistoryQuery.isFetching ? (
              <FormLoader />
            ) : (
              <LoginHistoryChart data={data?.data || []} />
            )}
          </Grid>
          <Grid item sm={12} md={12} xs={12}>
            <Divider sx={{ my: 3 }} />
            <LoginHistoryTable />
          </Grid>
        </Grid>
      </MainCard>
    </>
  );
};

export default LoginHistoryStatistics;
